// FE-1
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import ContentLayout from "app/layouts/ContentLayout";
import PageLoading from "app/components/CommonPage/PageLoading";
import InputText from "app/components/UI/Form/Input/InputText";
import PageError from "app/components/CommonPage/PageError";
import ConfirmDeleteDialog from "app/components/Dialog/ConfirmDeleteDialog";
import Button from "app/components/UI/Button";
import routePaths from "app/route/routePaths";
import { callApiStatus } from "app/constants";
import { customerServices } from "app/services/api";
import TrashBinSVG from "app/assets/svg/trashbin.svg";

import CustomerInfoForm from "../CustomerInfoForm";
import messages from "../../../services/api/messages";

const labelWidth = 146;

function UpdateCustomerPageContent(props) {
  const { customerBaseInfo } = props;

  const navigate = useNavigate();
  const formDataRef = useRef();
  const clickSubmit = () => {
    if (
      formDataRef.current &&
      typeof formDataRef.current.clickSubmit === "function"
    )
      formDataRef.current.clickSubmit();
  };

  const [updateStatus, setUpdateStatus] = useState(callApiStatus.idle);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const handleSubmitForm = (formData) => {
    const data = {
      customerId: customerBaseInfo.customerId,
      ...formData,
    };

    if (updateStatus === callApiStatus.loading) return;
    customerServices
      .updateInfo(data)
      .then((res) => {
        toast.success(messages.update(true));
        setUpdateStatus(callApiStatus.idle);
        navigate(routePaths.detailCustomer(res.data.id));
      })

      .catch((error) => {
        toast.error(messages.update(false));
        setUpdateStatus(callApiStatus.error);
      });
  };

  const handleDeleteCustomer = async () => {
    return await customerServices.deleteCustomer(customerBaseInfo.id);
  };

  return (
    <>
      <div className="p-4">
        <div className="flex justify-between pb-4">
          <div className="flex items-end">顧客情報</div>
          <div className="flex gap-4">
            <Button onClick={clickSubmit}>変更する</Button>
            <button
              type="button"
              onClick={() => setShowConfirmDelete(true)}
              className="center-flex"
            >
              <img
                className="w-[1.25em] h-[1.25em] cursor-pointer"
                alt=""
                src={TrashBinSVG}
              />
            </button>
          </div>
        </div>
        <div className="w-full h-auto">
          <div className="mb">
            <InputText
              id="customerId"
              value={customerBaseInfo.customerId || ""}
              label="お客様ID"
              labelWidth={labelWidth}
              disabled={true}
            />
            <InputText
              id="customerId"
              value={customerBaseInfo.companyNameKanji || ""}
              label="会社名"
              labelWidth={labelWidth}
              disabled={true}
            />
            <InputText
              id="customerId"
              value={customerBaseInfo.companyNameKana || ""}
              label="フリガナ"
              labelWidth={labelWidth}
              disabled={true}
            />
          </div>

          <CustomerInfoForm
            ref={formDataRef}
            handleSubmitForm={handleSubmitForm}
            customerBaseInfo={customerBaseInfo}
          />
        </div>
      </div>

      <ConfirmDeleteDialog
        open={showConfirmDelete}
        handleClose={() => setShowConfirmDelete(false)}
        confirmTitle="お客様の削除の確認"
        confirmMessage={
          <div className="flex flex-col items-center">
            <p>
              {customerBaseInfo.companyNameKanji}（ID:{" "}
              {customerBaseInfo.customerId}
              ）を削除をしますか？
            </p>
            <p>（削除したら復旧できないので、ご注意ください。）</p>
          </div>
        }
        deleteCompleteTitle="顧客が正常に削除されましたId"
        deleteCompleteBtnLabel="顧客IDの検索に戻る"
        deleteCompleteAction={() => navigate(routePaths.systemSettingsCustomer)}
        deleteAction={handleDeleteCustomer}
        handleDeleteSuccess={() => {
          navigate(routePaths.systemSettings);
          toast.success(messages.delete(true));
        }}
        handleDeleteError={() => {
          toast.error(messages.delete(false));
        }}
      />
    </>
  );
}

function UpdateCustomer(props) {
  const { id } = useParams();
  const [breadcrumb] = useOutletContext();

  const [loadInitState, setLoadInitState] = useState({
    isLoading: false,
    data: null,
    error: null,
  });

  const loadInit = useCallback(() => {
    setLoadInitState({
      isLoading: true,
      data: null,
      error: null,
    });

    customerServices
      .getDetail(id)
      .then((res) => {
        setLoadInitState({
          isLoading: false,
          data: res.data,
          error: null,
        });
      })
      .catch((error) => {
        toast.error(messages.callApiFail);
        setLoadInitState({
          isLoading: false,
          data: null,
          error: error,
        });
      });
  }, [id]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <ContentLayout
      sideMenuLinkOptions={[
        { linkTo: "/customer", linkTitle: "顧客情報の検索" },
        { linkTo: "/customer/create", linkTitle: "顧客情報の登録" },
        { linkTo: routePaths.topMenu, linkTitle: "トップへ戻る" },
      ]}
      breadcrumb={breadcrumb}
    >
      <div className="w-full h-full grow flex flex-col pt-2.5">
        {loadInitState.isLoading ? <PageLoading /> : null}

        {!loadInitState.isLoading && loadInitState.error ? <PageError /> : null}

        {!loadInitState.isLoading &&
        !loadInitState.error &&
        loadInitState.data ? (
          <UpdateCustomerPageContent
            id={id}
            customerBaseInfo={loadInitState.data}
          />
        ) : null}
      </div>
    </ContentLayout>
  );
}

export default UpdateCustomer;
